import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import usePostCheckoutState from "@hooks/usePostCheckoutState";

import { tokenInfoAtom } from "@/atoms/enrolAtom";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { AddonInterface } from "@/ts/interfaces/Checkout/AddonInterface";
import { GradeInterface } from "@/ts/interfaces/Checkout/GradeInterface";
import { PlanInterface } from "@/ts/interfaces/Checkout/PlanInterface";
import { ProductFamilyInterface } from "@/ts/interfaces/Checkout/ProductFamilyInterface";
import { mapQueryParamsDataToCheckoutAtom } from "@/utils/checkoutDataMapper";
import { getTrackingId, removeNullProperties } from "@/utils/utils";
import { studentValidationSchema } from "@/utils/validations/StudentValidationSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAtom, useSetAtom } from "jotai";

import { Flex } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { CheckoutPagesEnum } from "@ts/enums/CheckoutPagesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { CreateStudentFormDataInterface } from "@ts/interfaces/Enrol/StudentEntrolInterface";

import {
    checkoutAtom,
    isProvidedQueryParamsAtom,
    pageQueryParamsAtom
} from "@atoms/checkoutAtom";

import ContentLayout from "@layouts/ContentLayout";

import { CreateStudentForm } from "@components/CreateStudentForm";

const CreateNewStudentContainer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [searchParams, setSearchParams] = useSearchParams();
    const setIsProvidedQueryParams = useSetAtom(isProvidedQueryParamsAtom);
    const [tokenInfo] = useAtom(tokenInfoAtom);

    const productFamilyId = searchParams.get("family");
    const gradeName = searchParams.get("grade");
    const planId = searchParams.get("plan");
    const addonId = searchParams.get("addon");
    const coupon = searchParams.get("coupon");

    const checkoutContext = useContext(CheckoutContext);

    const { productFamilies } = checkoutContext;

    const [pathNavigateTo, setPathNavigateTo] = useState(
        CheckoutPageRoutesEnum.STATE_SELECT_PAGE
    );
    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();
    const { checkoutStateMutate, checkoutStateMutationState } =
        usePostCheckoutState(() => {
            navigate(routeWithQueryParams(pathNavigateTo));
        });

    const formMethods = useForm<CreateStudentFormDataInterface>({
        defaultValues: {
            firstName: checkout.student?.firstName
                ? checkout.student.firstName
                : tokenInfo.student_first_name,
            lastName: checkout.student?.lastName
                ? checkout.student.lastName
                : tokenInfo.student_last_name
        },
        resolver: yupResolver(studentValidationSchema),
        reValidateMode: "onChange",
        mode: "onChange"
    });

    const {
        handleSubmit,
        formState: { isValid, errors, touchedFields }
    } = formMethods;

    const onGoBack = () =>
        navigate(
            routeWithQueryParams(CheckoutPageRoutesEnum.STUDENT_SELECTION_PAGE)
        );

    const onGoNext = () =>
        navigate(
            routeWithQueryParams(CheckoutPageRoutesEnum.STATE_SELECT_PAGE)
        );

    const onSubmitCreateStudentForm = ({
        firstName,
        lastName
    }: CreateStudentFormDataInterface) => {
        let productFamily: ProductFamilyInterface | null = null;
        let grade: GradeInterface | null = null;
        let plan: PlanInterface | null = null;
        let addon: AddonInterface | null = null;

        const pathNavigateTo = CheckoutPageRoutesEnum.STATE_SELECT_PAGE;

        setPathNavigateTo(pathNavigateTo);

        /**
         * We try to construct the checkout state
         * from the data passed via query parameters.
         * Correctly generated checkout link with query parameter values is expected.
         */
        if (productFamilies !== null) {
            const mappedCheckoutAtom = mapQueryParamsDataToCheckoutAtom(
                {
                    addonId,
                    gradeName,
                    planId,
                    productFamilyId
                },
                productFamilies
            );

            productFamily = mappedCheckoutAtom.productFamily;
            grade = mappedCheckoutAtom.grade;
            plan = mappedCheckoutAtom.plan;
            addon = mappedCheckoutAtom.addon;
        }

        /**
         * Handling the default available products
         */
        let defaultAddon = addon;
        if (plan && !addon) {
            const availableAddons = plan.addons;
            if (availableAddons) {
                defaultAddon =
                    availableAddons.find(a => !a.isHighlighted) ?? null;
            }
        }

        setCheckout(prev => ({
            ...prev,
            productFamily,
            grade,
            plan,
            addon: defaultAddon,
            selectedStudent: null,
            student: {
                firstName,
                lastName
            },
            nextAttemptPage: CheckoutPagesEnum.STATE_SELECT_PAGE
        }));

        onSetupQueryParams({
            ...pageQueryParams,
            addon: addon ? addon.id : null,
            plan: plan ? plan.id : null,
            family: productFamily ? productFamily.id : null,
            grade: grade ? grade.name : null,
            coupon
        });
        onGoNext();
        checkoutStateMutate();
    };

    useEffect(() => {
        // Set all the query parameters in the URL which were updated in "pageQueryParams" atom at this point.
        setSearchParams(removeNullProperties(pageQueryParams));
        setIsProvidedQueryParams(false);
    }, [pageQueryParams]);

    return (
        <FormProvider {...formMethods}>
            <ContentLayout
                className={ClassesEnum.CreateNewStudentContainer}
                title={t(
                    "enrol.createStudentPage.title",
                    "Who are we enrolling today?"
                )}
                footerActionsProps={{
                    canGoNext: isValid,
                    isNextBtnLoading: checkoutStateMutationState.isPending,
                    onGoNext: handleSubmit(onSubmitCreateStudentForm),
                    onGoBack,
                    btnClassHierarchy: {
                        next: [
                            ClassesEnum.CreateNewStudentContainer,
                            ClassesEnum.Button
                        ],
                        back: [
                            ClassesEnum.CreateNewStudentContainer,
                            ClassesEnum.Button
                        ]
                    },
                    btnActions: {
                        next: "goNextFromCreateStudentPage",
                        back: "goBackFromCreateStudentPage"
                    }
                }}
            >
                <Flex direction="column" as="form" gap={10}>
                    <CreateStudentForm
                        errors={errors}
                        touchedFields={touchedFields}
                        data-tracking-id={getTrackingId(
                            [ClassesEnum.CreateNewStudentContainer],
                            "createStudentForm"
                        )}
                    />
                </Flex>
            </ContentLayout>
        </FormProvider>
    );
};

export default CreateNewStudentContainer;
